import { FC, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '@hooks/redux-custom';

function isActivePage(pathname: string, page: string): boolean {
  return pathname.toLowerCase().startsWith(`/${page.toLowerCase()}`);
}

const drawerWidth = 240;

export const NavBar: FC = () => {
  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const activeUser = useAppSelector((state) => state.activeUser.value);
  const history = useHistory();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const routes = [
    { name: 'customers', path: 'customers', module: 'customer' },
    { name: 'products', path: 'products', module: 'product' },
    { name: 'ava', path: 'ava', module: 'ava', disabled: !window.FEATURE_FLAGS.AVA_INTEGRATION_23, id: new Date().valueOf() },
    { name: 'webhooks', path: 'webhooks', module: 'webhook' },
    { name: 'templates', path: 'templates', module: 'templates' },
    { name: 'users', path: 'users', module: 'users' },
    { name: 'services', path: 'services', module: 'services' },
    { name: 'campaigns', path: 'campaigns', module: 'campaign' },
  ];

  const activeModules = activeUser?.user?.roles.filter((role: any) => role.role);

  const routeAuthenticated = (routeName: string) => {
    return activeModules?.find((module: any) => module.moduleName === routeName);
  };

  const handleClose = (path?: string) => {
    if (path) history.push(path);

    setOpen(false);
  };

  const getHeaderRouteName = () => {
    const l: string = location?.pathname.replace('/', '');

    const validRoutes: string[] = routes.map((route) => route.path);

    if (validRoutes.indexOf(l) === -1 && customer?.first_name) {
      return (
        <div
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-start',
          }}
        >
          <Typography variant="subtitle1">
            <Link to={`/customers`} rel="noopener noreferrer" style={{ textDecoration: 'none' }} role="row">
              Customers - &nbsp;
            </Link>
            {customer?.first_name ?? ''} {customer?.last_name}
          </Typography>
        </div>
      );
    }

    if (validRoutes.indexOf(l) !== -1)
      return <span style={{ textTransform: 'uppercase' }}>{location?.pathname.replace('/', ' ') ?? ''}</span>;
  };

  return (
    <>
      <AppBar position="sticky" data-testid="nav-bar" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Container maxWidth="lg">
          <Toolbar>
            <IconButton onClick={() => setOpen(!open)} size="small" data-testid="drawer-button">
              <MenuIcon fontSize="large" />
            </IconButton>
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="h1"
                noWrap={true}
                component="div"
                sx={{ flexGrow: 1, fontFamily: 'Knockout69', fontSize: 48, paddingX: 1 }}
              >
                JIMBO
              </Typography>
              <Button
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'primary.light' } }}
                data-testid="signout-btn"
                onClick={() => history.push('/signout')}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        variant="temporary"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
        data-testid="drawer"
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', height: '100%' }}>
          <List sx={{ paddingTop: 0 }}>
            {routes
              .filter((route) => routeAuthenticated(route?.module))
              .map((route) => {
                return (
                  !route.disabled && (
                    <ListItem
                      key={`${route.name}-${new Date().getTime()}`}
                      disablePadding
                      sx={{
                        bgcolor: isActivePage(location.pathname, route.path) ? 'primary.light' : '',
                        borderBottom: '1px solid lightgrey',
                      }}
                    >
                      <ListItemButton onClick={() => handleClose(`/${route.path}`)}>
                        <ListItemText
                          primary={route.name}
                          data-testid={`${route.name}-link`}
                          primaryTypographyProps={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                );
              })}
          </List>
        </Box>
      </Drawer>
      <Box py={1.5} sx={{ alignItems: 'center', display: 'flex' }} bgcolor="primary.light">
        <Container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" data-testid="header-route-name">
            {getHeaderRouteName()}
          </Typography>
        </Container>
      </Box>
    </>
  );
};
